import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  BarChart3,
  PieChart,
  LineChart as LineChartIcon,
  Table2,
  TrendingUp,
  LayoutGrid,
} from 'lucide-react'
import type { ReportTemplate, ChartType } from './types'
import { REPORT_TEMPLATES } from './types'

const CHART_ICONS: Record<ChartType, typeof BarChart3> = {
  bar: BarChart3,
  line: LineChartIcon,
  area: TrendingUp,
  pie: PieChart,
  table: Table2,
}

const CATEGORY_LABELS: Record<string, string> = {
  sales: 'Sales',
  leads: 'Leads',
  activities: 'Activities',
  support: 'Support',
  accounts: 'Accounts',
}

interface TemplateGalleryProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelectTemplate: (template: ReportTemplate) => void
}

export function TemplateGallery({ open, onOpenChange, onSelectTemplate }: TemplateGalleryProps) {
  const categories = [...new Set(REPORT_TEMPLATES.map((t) => t.category))]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" aria-label="Browse report templates">
          <LayoutGrid className="mr-2 h-4 w-4" aria-hidden="true" />
          Templates
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Report Templates</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Choose a template to get started quickly. You can customize it after.
          </p>
        </DialogHeader>
        <div className="space-y-6 mt-4">
          {categories.map((category) => (
            <div key={category}>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                {CATEGORY_LABELS[category]} (
                {REPORT_TEMPLATES.filter((t) => t.category === category).length})
              </h3>
              <div
                className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                role="listbox"
                aria-label={`${CATEGORY_LABELS[category]} templates`}
              >
                {REPORT_TEMPLATES.filter((t) => t.category === category).map((template) => {
                  const Icon = CHART_ICONS[template.chartType]
                  return (
                    <Card
                      key={template.id}
                      className="cursor-pointer hover:border-primary transition-colors"
                      role="option"
                      aria-selected={false}
                      tabIndex={0}
                      onClick={() => {
                        onSelectTemplate(template)
                        onOpenChange(false)
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          onSelectTemplate(template)
                          onOpenChange(false)
                        }
                      }}
                    >
                      <CardContent className="p-4 flex items-start gap-3">
                        <div className="p-2 bg-muted rounded-md">
                          <Icon className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{template.name}</span>
                            <Badge variant="outline" className="text-xs capitalize shrink-0">
                              {template.chartType}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {template.description}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  )
}
